import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import { Subscription } from 'rxjs';

export const RKB_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  },
};

@Component({
  selector: 'rk-date-time-picker',
  imports: [
    MatDatepickerModule,
    MatTimepickerModule,
    ReactiveFormsModule,
    TranslateModule,
    FaIconComponent,
  ],
  templateUrl: './date-time-picker.component.html',
  styleUrl: './date-time-picker.component.scss',
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: RKB_DATE_FORMATS },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (translateService: TranslateService) => {
        const lang = translateService.currentLang;
        switch (lang) {
          case 'fr-FR':
            return 'fr';
          case 'es-ES':
            return 'es';
          case 'de-DE':
            return 'de';
          default:
            return 'en';
        }
      },
      deps: [TranslateService],
    },
  ],
})
export class DateTimePickerComponent implements OnInit, OnDestroy {
  @Input() dateControl!: FormControl;
  dateTimeForm: FormGroup;
  minTime: string | null = null;
  minDate: Moment = moment();

  private readonly _adapter = inject<DateAdapter<Moment>>(DateAdapter);
  private readonly subscription = new Subscription();

  constructor(
    private readonly translateService: TranslateService,
    private readonly fb: FormBuilder,
  ) {
    this.dateTimeForm = this.fb.group({
      date: new FormControl('', {
        updateOn: 'blur',
      }),
      time: new FormControl(''),
    });
  }

  ngOnInit(): void {
    // set locale
    const locale = this.translateService.currentLang;
    let momentLocale = 'en';

    switch (locale) {
      case 'fr-FR':
        momentLocale = 'fr';
        break;
      case 'es-ES':
        momentLocale = 'es';
        break;
      case 'de-DE':
        momentLocale = 'de';
        break;
    }

    this._adapter.setLocale(momentLocale);
    moment.locale(momentLocale);
    // initialize form with existing value if any
    if (this.dateControl.value) {
      const date = moment(this.dateControl.value);
      this.dateTimeForm.patchValue({
        date,
        time: date,
      });
      this.updateMinTime(date);
    }

    // subscribe to date changes
    this.subscription.add(
      this.dateTimeForm.get('date').valueChanges.subscribe((date: Moment) => {
        if (date?.isValid()) {
          let combinedDate = moment(date);
          const time = this.dateTimeForm.get('time')?.value;

          this.updateMinTime(combinedDate);

          if (time && moment(time)?.isValid()) {
            const timeDate = moment(time);
            combinedDate.hours(timeDate.hours());
            combinedDate.minutes(timeDate.minutes());
            combinedDate = this.roundToNext15Minutes(combinedDate);
          } else {
            const now = moment();
            combinedDate.hours(now.hours());
            combinedDate.minutes(now.minutes());
            combinedDate = this.roundToNext15Minutes(combinedDate);
          }
          this.dateControl.setValue(combinedDate);
        }
      }),
    );

    // subscribe to time changes
    this.subscription.add(
      this.dateTimeForm.get('time').valueChanges.subscribe((time: string | Moment) => {
        if (time && moment(time).isValid()) {
          const timeDate = moment(time);
          const date = this.dateTimeForm.get('date').value;

          // if time is selected but no date, use today's date or tomorrow if time is before now
          let baseDate: Moment;
          if (date && moment(date).isValid()) {
            baseDate = moment(date);
          } else {
            const now = moment();
            const selectedTime = moment(time);
            const todayWithSelectedTime = moment()
              .hours(selectedTime.hours())
              .minutes(selectedTime.minutes());

            if (todayWithSelectedTime.isBefore(now)) {
              baseDate = moment().add(1, 'day');
            } else {
              baseDate = moment();
            }
          }

          baseDate.hours(timeDate.hours());
          baseDate.minutes(timeDate.minutes());

          this.dateControl.setValue(baseDate);

          if (!date) {
            this.dateTimeForm.patchValue(
              {
                date: baseDate,
                time: timeDate, // Preserve the exact time value to maintain time picker selection
              },
              { emitEvent: false },
            );
            this.updateMinTime(baseDate);
          }
        }
      }),
    );

    // subscribe to parent control changes
    this.subscription.add(
      this.dateControl.valueChanges.subscribe(value => {
        if (value) {
          const date = moment(value);
          if (date.isValid()) {
            this.updateMinTime(date);
            this.dateTimeForm.patchValue(
              {
                date,
                time: date,
              },
              { emitEvent: false },
            );
          }
        }
      }),
    );
  }

  private isSameDay(date1: Moment, date2: Moment): boolean {
    return date1.isSame(date2, 'day');
  }

  private updateMinTime(selectedDate: Moment | null): void {
    if (!selectedDate?.isValid()) {
      this.minTime = null;

      return;
    }

    const now = moment();
    const minDateTime = this.minDate || now;

    if (this.isSameDay(selectedDate, minDateTime)) {
      const referenceDate =
        this.roundToNext15Minutes(this.minDate) || this.roundToNext15Minutes(now);
      this.minTime = referenceDate.format('HH:mm');
    } else {
      this.minTime = null;
    }
  }

  private roundToNext15Minutes(date: Moment): Moment {
    const minutes = date.minutes();
    const remainder = minutes % 15;
    if (remainder > 0) {
      return date.clone().add(15 - remainder, 'minutes');
    }

    return date;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
